import {
  ILayerPerformanceCriteriaImperial,
  IPerRoadLayerImperial,
  IPerRoadScenarioImperial,
  LayerPerformanceCriteriaType,
  PerRoadLayerType,
} from '@/modules/perRoad/interfaces/perRoad';
import {between, minValue, required} from 'vuelidate/lib/validators';
import {poissonRatios} from '@/modules/perRoad/lib/poissonRatio';
import {createValidatorsForDistribution} from '@/modules/lcca/lib/validations';

const createPerformanceCriteriaValidators = (d: ILayerPerformanceCriteriaImperial) => {
  if (d.criteria === LayerPerformanceCriteriaType.STRAIN_HORIZONTAL_DISTRIBUTION && d.enabled && d.distributionPercentiles) {
    return {
      distribution_ustrain: (function () {
        const itemsToValidate = d.distribution_ustrain || [];
        const itemList: { [index: number]: any } = {};
        itemsToValidate.forEach((m, i) => {
          itemList[i] = {
            required,
            minValue: minValue(0),
          };
        });
        return itemList;
      }()),
    };
  }
  if (d.criteria === LayerPerformanceCriteriaType.STRAIN_HORIZONTAL && d.enabled) {
    return {
      strainThreshold_ustrain: {
        required,
      },
      // k1: {
      //   required,
      //   minValue: minValue(0),
      //
      // },
      // k2: {
      //   required,
      //   minValue: minValue(0),
      // },
    };
  }

  if (d.criteria === LayerPerformanceCriteriaType.STRAIN_VERTICAL && d.enabled) {
    return {
      strainThreshold_ustrain: {
        required,
      },
    };
  }

  return {};
};

export const createPerRoadLayerValidations = (layer: IPerRoadLayerImperial, page: number) => ({
  name: {
    required,
  },
  type: {
    required,
  },
  pgGradeHigh: (function () {
    if (layer.type === PerRoadLayerType.AC) {
      return {
        required,
      };
    }
    return {};
  }()),
  pgGradeLow: (function () {
    if (layer.type === PerRoadLayerType.AC) {
      return {
        required,
      };
    }
    return {};
  }()),

  poissonRatio: {
    required,
    between: between(
      poissonRatios[layer.type] ? poissonRatios[layer.type].min : 0,
      poissonRatios[layer.type] ? poissonRatios[layer.type].max : 1,
    ),
  },

  modulus_psi: (function () {
    const itemsToValidate = layer.modulus_psi;
    const itemList: { [index: number]: any } = {};
    itemsToValidate.forEach((s, i) => {
      itemList[i] = createValidatorsForDistribution(s);
    });
    return itemList;
  }()),

  performanceCriteria: (function () {
    const itemsToValidate = layer.performanceCriteria;
    const itemList: { [index: number]: any } = {};
    if (page === 4 && layer.performanceCriteria.length > 0) {
      itemsToValidate.forEach((s, i) => {
        itemList[i] = createPerformanceCriteriaValidators(s);
      });
    }
    return itemList;
  }()),


});

export const createPerRoadValidations = (scenario: IPerRoadScenarioImperial, page: number) => ({
  scenarioImperial: {
    /*
     Scenario Info
     */
    name: {
      required,
    },

    seasons: (function () {
      const seasonsToValidate = scenario.seasons;
      const seasonList: { [index: number]: any } = {};
      seasonsToValidate.forEach((s, i) => {
        seasonList[i] = {
          duration_weeks: {
            required,
            between: between(0, 52),
          },
          temp_degF: {
            required,
            between: between(-100, 200),
          },
        };
      });
      return seasonList;
    }()),

    /*
     Traffic
     */
    twoWayAadt: {
      required,
      minValue: minValue(0),
    },

    pctTrucks: {
      required,
      between: between(0, 100),
    },

    pctTruckGrowthPerYear: {
      required,
      between: between(0, 100),
    },

    pctTrucksInDesignLane: {
      required,
      between: between(0, 100),
    },

    directionalDistribution: {
      required,
      between: between(0, 100),
    },

    roadwayClassification: {
      required,
    },

    vehiclesPctAadt: (function () {
      const itemsToValidate = scenario.vehiclesPctAadt;
      const itemList: { [index: number]: any } = {};
      itemsToValidate.forEach((s, i) => {
        itemList[i] = {
          required,
          minValue: minValue(0),
        };
      });
      return itemList;
    }()),

    vehicleAxlesSingle: (function () {
      const itemsToValidate = scenario.vehicleAxlesSingle;
      const itemList: { [index: number]: any } = {};
      itemsToValidate.forEach((s, i) => {
        itemList[i] = {
          required,
          minValue: minValue(0),
        };
      });
      return itemList;
    }()),

    vehicleAxlesTandem: (function () {
      const itemsToValidate = scenario.vehicleAxlesTandem;
      const itemList: { [index: number]: any } = {};
      itemsToValidate.forEach((s, i) => {
        itemList[i] = {
          required,
          minValue: minValue(0),
        };
      });
      return itemList;
    }()),

    vehicleAxlesTridem: (function () {
      const itemsToValidate = scenario.vehicleAxlesTridem;
      const itemList: { [index: number]: any } = {};
      itemsToValidate.forEach((s, i) => {
        itemList[i] = {
          required,
          minValue: minValue(0),
        };
      });
      return itemList;
    }()),

    axleLoadDistSingle: (function () {
      const itemsToValidate = scenario.axleLoadDistSingle;
      const itemList: { [index: number]: any } = {};
      itemsToValidate.forEach((s, i) => {
        itemList[i] = {
          required,
          minValue: minValue(0),
        };
      });
      return itemList;
    }()),

    axleLoadDistTandem: (function () {
      const itemsToValidate = scenario.axleLoadDistTandem;
      const itemList: { [index: number]: any } = {};
      itemsToValidate.forEach((s, i) => {
        itemList[i] = {
          required,
          minValue: minValue(0),
        };
      });
      return itemList;
    }()),

    axleLoadDistTridem: (function () {
      const itemsToValidate = scenario.axleLoadDistTridem;
      const itemList: { [index: number]: any } = {};
      itemsToValidate.forEach((s, i) => {
        itemList[i] = {
          required,
          minValue: minValue(0),
        };
      });
      return itemList;
    }()),

    /*
     Layers
     */
    layers: (function () {
      const itemsToValidate = scenario.layers;
      const itemList: { [index: number]: any } = {};
      itemsToValidate.forEach((s, i) => {
        itemList[i] = createPerRoadLayerValidations(s, page);
      });
      return itemList;
    }()),

    /*
     Performance Criteria
     */


  },

  /*
   Traffic page elements
   */
  singlePct: {
    required,
    between: between(0, 100),
  },
  tandemPct: {
    required,
    between: between(0, 100),
  },
  tridemPct: {
    required,
    between: between(0, 100),
  },
  steerPct: {
    required,
    between: between(0, 100),
  },

  normalizeRequired: {
    // Ensure that normalization doesn't need to happen
    // @ts-ignore
    normalizeNotRequired: (value) => value === false,
  },

  /*
   Overall page validation
   */
  1: [
    'scenarioImperial.name',
    'scenarioImperial.seasons',
  ],
  2: [
    'scenarioImperial.twoWayAadt',
    'scenarioImperial.pctTrucks',
    'scenarioImperial.pctTrucksInDesignLane',
    'scenarioImperial.directionalDistribution',
    'scenarioImperial.roadwayClassification',
    'scenarioImperial.vehiclesPctAadt',
    'scenarioImperial.vehicleAxlesSingle',
    'scenarioImperial.vehicleAxlesTandem',
    'scenarioImperial.vehicleAxlesTridem',
  ],
  3: (function () {
    if (page === 4) {
      return [];
    }
    return [
      'scenarioImperial.layers',
    ];
  }()),
  4: [
    'scenarioImperial.layers',
  ],
  5: [
    '1',
    '2',
    '3',
    '4',
  ],
});
